import debounce from 'lodash/debounce';
import {jsx, ParamComponent} from 'panel';

import {NoExtension, Section, SectionItem} from 'common/components/items-menu/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {reporter} from 'common/report/error-reporting';
import {getProjectId} from 'common/report/util';
import {useTemplateSearch} from 'common/report/util/feature-sampling';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getBrowseUrl,
  getDisplayContext,
  getSearchContexts,
  getSearchIconFromDisplayContext,
  isBrowseUrl,
  searchUtils,
} from 'common/report/util/search';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  getTrackingPropertiesForResult,
  getTrackingPropertiesForResultsLoad,
  SearchSource,
  SearchTrackedProperty,
  TypeaheadTrackedEvent,
} from 'common/report/util/search/tracking';
import {
  RecentSearchItemType,
  SearchDisplayContext,
  SearchDisplayContextDisplayName,
  SearchQueryFilters,
  SearchQuerySort,
  SearchResult,
  SearchSortKey,
  SearchSortOrder,
} from 'common/report/util/search/types';
import activeColorScheme from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {findKeyByValue} from 'common/util/typescript';
import {truncate} from 'common/util/string';
import {MixpanelUserItem} from 'common/widgets/user-list/types';
import {UserType} from 'common/widgets/user-list/constants';
import {UserListEl} from 'common/widgets/user-list';
import {IconSizeType} from 'common/constants';
import {SearchInputEl} from 'common/widgets/search-input';
import {InputTheme} from 'common/components/input/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {UserIconEl} from 'common/widgets/user-icon';
import {GlobalSearchController} from 'common/report/model-resources/global-search-controller';

const GLOBAL_SEARCH_SHOW_ALL = `Show all results`;
const MAX_CREATORS_SHOWN = 2;
const MAX_RESULTS_SHOWN = 3;
const CREATOR_SEARCH_MIN_LENGTH = 3;

type GlobalSearchParams = {
  currentUrlPath: string;
};

type GlobalSearchJsxProps = {
  params: GlobalSearchParams;
};

enum GlobalSearchItemType {
  Creator = `creator`,
  Recent = `recent`,
  Entity = `entity`,
  BrowseAll = `browse-all`,
  ReadOnly = `read-only`,
}

type CreatorSearchSectionItem = SectionItem<TopCreator, {type: GlobalSearchItemType.Creator}>;
type EntitySearchSectionItem = SectionItem<SearchResult, {type: GlobalSearchItemType.Entity}>;
type BrowseAllSearchSectionItem = SectionItem<string, {type: GlobalSearchItemType.BrowseAll}>;
type ReadOnlySearchSectionItem = SectionItem<string, {type: GlobalSearchItemType.ReadOnly}>;

type SearchSectionItem =
  | EntitySearchSectionItem
  | BrowseAllSearchSectionItem
  | ReadOnlySearchSectionItem
  | CreatorSearchSectionItem;

type SearchSection = Section<any, NoExtension, SearchSectionItem>;

interface GlobalSearchState {
  allowRecentItems?: boolean;
  errored?: boolean;
  open: boolean;
  loading?: boolean;
  searchedText?: string;
  searchResults: SearchResult[];
  searchInputEl?: HTMLElement;
}

function userListRenderer(item: EntitySearchSectionItem) {
  const users: MixpanelUserItem[] = [
    {
      name: item.value.user,
      id: item.value.user_id,
      email: item.value.email,
      type: UserType.Mixpanel,
    },
  ];

  return (
    <UserListEl
      attrs={{
        users,
        'max-icons-displayed': 1,
        'user-count-override': item.value.unique_count,
        size: IconSizeType.Small,
      }}
    />
  );
}

function leftIconRenderer({icon, tooltipText, isErrored}: {icon: string; tooltipText?: string; isErrored?: boolean}) {
  let color = null;
  if (isErrored) {
    color = activeColorScheme.red1;
  }

  const iconComponent = <SvgIconEl attrs={{icon, color}} style={{margin: `8px`}} />;
  if (tooltipText) {
    return (
      <div style={{display: `flex`}}>
        {iconComponent}
        <TooltipEl attrs={{placement: Placement.Top}}>{tooltipText}</TooltipEl>
      </div>
    );
  }
  return iconComponent;
}

export class GlobalSearch extends ParamComponent<GlobalSearchParams, GlobalSearchState, IronContextRegistry> {
  get config() {
    return {
      template: () => {
        return (
          <SearchInputEl
            attrs={{elref: `searchInputCmp`}}
            params={{
              enableShortcut: true,
              onEnterPressed: () => {
                this.track(TypeaheadTrackedEvent.ShowAllSelected, {'Pressed Enter': true});
                this.redirectToBrowsePage();
              },
              onItemClick: this.helpers.handleSearchSelection,
              onClearClick: () => {
                if (isBrowseUrl(this.getUrlManager())) {
                  this.redirectToBrowsePage();
                }
              },
              onMenuOpen: () => {
                this.track(TypeaheadTrackedEvent.OpenTypeahead);
                this.handleSearchedTextUpdate(this.state.searchedText);
              },
              onSearchedTextUpdate: (searchedText, isSuggestedItem) => {
                if (isSuggestedItem) {
                  this.track(TypeaheadTrackedEvent.RecentQuerySelected);
                }
                this.handleSearchedTextUpdate(searchedText);
              },
              onTrackEvent: (event, props) => this.track(event, props ?? {}),
              searchedText: this.state.searchedText,
              sections: this.getSearchSections(),
              theme: InputTheme.Chrome,
            }}
          />
        );
      },
      contexts: [`projectTopCreators` as const, `urlManager` as const, `globalSearchController` as const],
      defaultState: {
        allowRecentItems: true,
        errored: false,
        loading: false,
        searchResults: [],
        searchedText: DEFAULT_SEARCH_QUERY_STRING,
        open: false,
      },
      params: {
        currentUrlPath: String,
      },
      helpers: {
        handleSearchSelection: (item: SearchSectionItem) => {
          if (item.type === GlobalSearchItemType.Entity) {
            this.update({searchResults: [], searchedText: ``});
            this.track(TypeaheadTrackedEvent.ResultSelected, getTrackingPropertiesForResult(item.value));
          } else if (item.type === GlobalSearchItemType.BrowseAll) {
            this.track(TypeaheadTrackedEvent.ShowAllSelected);
          } else if (item.type === GlobalSearchItemType.Creator) {
            this.update({searchResults: [], searchedText: ``});
            const recentItem = {
              id: item.value.id,
              userEmail: item.value.email,
              key: `user-${item.value.id}`,
              label: item.value.name,
              type: RecentSearchItemType.User,
            };
            searchUtils.addRecentItem(recentItem);
            this.track(TypeaheadTrackedEvent.CreatorFilterSelected);
          }
        },
      },
      hooks: {
        preUpdate: (_newState, newParams?: GlobalSearchParams) => {
          if (newParams && newParams.currentUrlPath !== this.params.currentUrlPath) {
            this.setDefaultSearchText();
          }
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setDefaultSearchText();
  }

  resourceCustomSubscriptions = new Map([
    [
      GlobalSearchController,
      () => {
        this.setDefaultSearchText();
      },
    ],
  ]);

  getSearchParamsContext() {
    return this.getContext(`globalSearchController`);
  }

  setDefaultSearchText() {
    const persistedSearchedText = this.getSearchParamsContext().getSearchedText();
    if (isBrowseUrl(this.getUrlManager())) {
      if (persistedSearchedText && persistedSearchedText !== this.state.searchedText) {
        this.update({searchedText: persistedSearchedText});
      }
    } else {
      this.update({searchedText: ``});
    }
  }

  handleSearchedTextUpdate(searchedText: string): void {
    if (this.state.searchedText !== searchedText || !this.state.searchResults.length) {
      this.update({searchedText, loading: true});
      this.executeSearch(searchedText);
    }
  }

  redirectToBrowsePage() {
    this.getUrlManager().setLocation(
      getBrowseUrl(this.getUrlManager(), {queryParam: {q: this.state.searchedText || ``}}),
    );
  }

  private getUrlManager() {
    return this.getContext(`urlManager`);
  }

  getSearchSections(): SearchSection[] {
    const shouldLinkSearchedText = this.state.searchedText !== DEFAULT_SEARCH_QUERY_STRING;

    return [
      ...this.getSearchCreatorSection(),
      ...this.getSearchResultSections(),
      ...(useTemplateSearch() ? this.getTemplateSearchResultSections() : []),
      ...(shouldLinkSearchedText ? [this.getBrowseItem()] : []),
    ];
  }

  private getBrowseItem(): SearchSection {
    const href = getBrowseUrl(this.getUrlManager(), {queryParam: {q: this.state.searchedText || ``}});
    return {
      isLoading: false,
      iconRenderer: () => leftIconRenderer({icon: `arrow-diagonal`}),
      items: [
        {
          label: GLOBAL_SEARCH_SHOW_ALL,
          type: GlobalSearchItemType.BrowseAll,
          href,
        },
      ],
    };
  }

  private getSearchSortKey(searchedText: string): SearchSortKey | null {
    if (searchedText === DEFAULT_SEARCH_QUERY_STRING) {
      if (this.getContext(`projectTopCreators`).hasViewedContent()) {
        return SearchSortKey.RecentlyViewed;
      } else {
        return SearchSortKey.Popularity;
      }
    }
    return null;
  }

  private executeSearch = debounce(async (searchedText) => {
    this.update({searchResults: [], loading: true});

    const queryFilters: SearchQueryFilters = {
      email: [],
      type: getSearchContexts().map((value) => ({value, negated: false})),
    };

    const trackingProps = {
      [SearchTrackedProperty.OwnerFilters]: [],
      [SearchTrackedProperty.ReportFilters]: [],
      [SearchTrackedProperty.SearchQuery]: searchedText,
    };
    try {
      const sortKey = this.getSearchSortKey(searchedText);
      const querySort: SearchQuerySort = sortKey ? {key: sortKey, order: SearchSortOrder.Descending} : null;

      const searchResults = await searchUtils.executeSearch({
        queryFilters,
        querySort,
        searchedText,
        limit: 100,
      });
      if (searchedText === this.state.searchedText && searchResults) {
        this.update({loading: false, searchResults});

        this.track(TypeaheadTrackedEvent.SearchResultLoaded, {
          ...trackingProps,
          ...getTrackingPropertiesForResultsLoad(searchResults),
        });
      }
    } catch (err) {
      reporter.error(err);
      this.update({errored: true, loading: false});
    }
  }, 200);

  private getTemplateSearchResultSections(): SearchSection[] {
    const {loading, searchedText} = this.state;
    const items = [...searchUtils.getTemplateItems(searchedText)];
    return items.length === 0 || loading
      ? []
      : this.processSearchResultItems({items, label: `Templates`, loading, maxSearchResults: items.length});
  }

  private getSearchResultSections(): SearchSection[] {
    const {loading, searchResults, searchedText} = this.state;
    const items = searchResults.map(
      (item: SearchResult): EntitySearchSectionItem => {
        return {
          href: item.link,
          icon: getSearchIconFromDisplayContext(getDisplayContext(item)),
          label: truncate(item.name, 50),
          subLabel: item.dashboard,
          type: GlobalSearchItemType.Entity,
          value: item,
        };
      },
    );
    const label =
      {[SearchSortKey.Popularity]: `Most Viewed`, [SearchSortKey.RecentlyViewed]: `Recently Viewed`}[
        this.getSearchSortKey(searchedText)
      ] ?? `Boards & Reports`;
    return this.processSearchResultItems({items, label, loading, maxSearchResults: MAX_RESULTS_SHOWN});
  }

  private processSearchResultItems({items, label, loading, maxSearchResults}): SearchSection[] {
    const {errored} = this.state;

    const results: SearchSection = {
      label: loading ? null : label,
      isLoading: loading,
      hideTopDivider: true,
      actionsRenderer: (item: EntitySearchSectionItem) =>
        !errored && items.length > 0 ? userListRenderer(item) : null,
      iconRenderer: (item: SearchSectionItem) => {
        if (item.type === GlobalSearchItemType.Entity) {
          return leftIconRenderer({
            icon: item.icon,
            tooltipText: SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, item.value.report)],
            isErrored: errored,
          });
        }
        return null;
      },
      items:
        items.length > 0
          ? items.slice(0, maxSearchResults)
          : [
              {
                icon: errored ? `warning` : `search`,
                isReadOnly: true,
                label: errored ? `Something went wrong` : `No items match your search`,
                subLabel: errored ? `Try another search, or contact support if this error persists.` : null,
                type: GlobalSearchItemType.ReadOnly,
              },
            ],
    };

    return [results];
  }

  private getSearchCreatorSection(): SearchSection[] {
    const creators = this.getContext(`projectTopCreators`).getCreators();
    if (this.state.searchedText?.length < CREATOR_SEARCH_MIN_LENGTH || !creators) {
      return [];
    }

    const rank = (creator: TopCreator) => fuzzyMatch(creator.name, this.state.searchedText)?.score ?? 0;

    const rankedCreators = creators
      .filter((creator) => rank(creator) > 0)
      .sort((creator1, creator2) => rank(creator2) - rank(creator1))
      .slice(0, MAX_CREATORS_SHOWN);

    const items: CreatorSearchSectionItem[] = rankedCreators.map((creator: TopCreator) => {
      return {
        label: creator.name,
        type: GlobalSearchItemType.Creator,
        value: creator,
        href: getBrowseUrl(this.getUrlManager(), {queryParam: {email: creator.email}}),
      };
    });

    return [
      {
        label: `Creators`,
        items,
        iconRenderer: (item: CreatorSearchSectionItem) => {
          return (
            <UserIconEl
              attrs={{
                id: item.value.id,
                email: item.value.email,
                name: item.value.name,
                size: IconSizeType.Small,
              }}
            />
          );
        },
      },
    ];
  }

  private track(eventName: string, props: Obj = {}) {
    mixpanel.track(eventName, {
      ...this.getCommonTrackingProperties(),
      ...props,
      project_id: getProjectId(),
    });
  }

  private getCommonTrackingProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: this.state.searchedText
        ? ContentDiscoveryType.SearchQuery
        : ContentDiscoveryType.Browse,
    };
  }
}

export const GlobalSearchEl = defineAndMakeJsxFactory<GlobalSearchJsxProps>(`mp-global-search`, GlobalSearch);
